import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';

gsap.registerPlugin(Draggable);

const DEBOUNCE_MS = 500;
const STEP_CENTS = 100;

export default class FilterRange extends HTMLElement {
  private slider: HTMLDivElement | null = null;
  private trackActive: HTMLDivElement | null = null;
  private thumbMin: HTMLButtonElement | null = null;
  private thumbMax: HTMLButtonElement | null = null;
  private displayMin: HTMLSpanElement | null = null;
  private displayMax: HTMLSpanElement | null = null;
  private inputMin: HTMLInputElement | null = null;
  private inputMax: HTMLInputElement | null = null;
  private form: HTMLFormElement | null = null;

  private minDraggable: Draggable | null = null;
  private maxDraggable: Draggable | null = null;

  private rangeMax = 0;
  private minValue = 0;
  private maxValue = 0;
  private minParamName = '';
  private maxParamName = '';

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private onResizeBound = this.onResize.bind(this);
  private onThumbMinKeyDownBound = (event: KeyboardEvent) => this.onThumbKeyDown(event, 'min');
  private onThumbMaxKeyDownBound = (event: KeyboardEvent) => this.onThumbKeyDown(event, 'max');

  connectedCallback(): void {
    this.slider = this.querySelector('[data-filter-range-ref="slider"]');
    this.trackActive = this.querySelector('[data-filter-range-ref="track-active"]');
    this.thumbMin = this.querySelector('[data-filter-range-ref="thumb-min"]');
    this.thumbMax = this.querySelector('[data-filter-range-ref="thumb-max"]');
    this.displayMin = this.querySelector('[data-filter-range-ref="display-min"]');
    this.displayMax = this.querySelector('[data-filter-range-ref="display-max"]');
    this.inputMin = this.querySelector('[data-filter-range-ref="input-min"]');
    this.inputMax = this.querySelector('[data-filter-range-ref="input-max"]');
    this.form = this.closest('form');

    this.rangeMax = parseInt(this.dataset.rangeMax || '0', 10);
    this.minValue = parseInt(this.dataset.minValue || '0', 10);
    this.maxValue = parseInt(this.dataset.maxValue || `${this.rangeMax}`, 10);
    this.minParamName = this.dataset.minParamName || '';
    this.maxParamName = this.dataset.maxParamName || '';

    this.clampValues();
    this.syncUi(false);
    this.initDraggables();
    this.addEventListeners();
  }

  private clampValues(): void {
    this.minValue = this.clampCents(this.minValue);
    this.maxValue = this.clampCents(this.maxValue);

    if (this.minValue > this.maxValue) {
      this.minValue = this.maxValue;
    }
  }

  private clampCents(value: number): number {
    return Math.min(Math.max(0, this.snap(value)), this.rangeMax);
  }

  private snap(value: number): number {
    if (STEP_CENTS <= 0) {
      return value;
    }

    return Math.round(value / STEP_CENTS) * STEP_CENTS;
  }

  private getTrackWidth(): number {
    return this.slider?.getBoundingClientRect().width ?? 0;
  }

  private valueToX(value: number): number {
    const trackWidth = this.getTrackWidth();

    if (this.rangeMax <= 0 || trackWidth <= 0) {
      return 0;
    }

    return (value / this.rangeMax) * trackWidth;
  }

  private xToValue(x: number): number {
    const trackWidth = this.getTrackWidth();

    if (this.rangeMax <= 0 || trackWidth <= 0) {
      return 0;
    }

    return this.snap((x / trackWidth) * this.rangeMax);
  }

  private centsToDecimal(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  private isDefaultRange(): boolean {
    return this.minValue === 0 && this.maxValue === this.rangeMax;
  }

  private syncUi(shouldCommit: boolean): void {
    this.updateThumbPositions();
    this.updateAriaValues();
    this.syncFormInputs();
    this.updateDragBounds();

    if (shouldCommit) {
      this.commitChangeDebounced();
    }
  }

  private updateThumbPositions(): void {
    if (!this.thumbMin || !this.thumbMax || !this.trackActive) {
      return;
    }

    const minX = this.valueToX(this.minValue);
    const maxX = this.valueToX(this.maxValue);

    gsap.set(this.thumbMin, { x: minX });
    gsap.set(this.thumbMax, { x: maxX });

    if (this.minDraggable) {
      this.minDraggable.x = minX;
    }

    if (this.maxDraggable) {
      this.maxDraggable.x = maxX;
    }

    const trackWidth = this.getTrackWidth();

    if (trackWidth <= 0) {
      return;
    }

    const minPercent = (minX / trackWidth) * 100;
    const maxPercent = (maxX / trackWidth) * 100;

    this.trackActive.style.left = `${minPercent}%`;
    this.trackActive.style.width = `${Math.max(0, maxPercent - minPercent)}%`;
  }

  private updateAriaValues(): void {
    if (this.thumbMin) {
      this.thumbMin.setAttribute('aria-valuemin', '0');
      this.thumbMin.setAttribute('aria-valuemax', this.maxValue.toString());
      this.thumbMin.setAttribute('aria-valuenow', this.minValue.toString());
    }

    if (this.thumbMax) {
      this.thumbMax.setAttribute('aria-valuemin', this.minValue.toString());
      this.thumbMax.setAttribute('aria-valuemax', this.rangeMax.toString());
      this.thumbMax.setAttribute('aria-valuenow', this.maxValue.toString());
    }
  }

  private syncFormInputs(): void {
    const isDefault = this.isDefaultRange();

    if (this.displayMin) {
      this.displayMin.textContent = isDefault ? '0' : this.centsToDecimal(this.minValue);
    }

    if (this.displayMax) {
      this.displayMax.textContent = isDefault ? this.centsToDecimal(this.rangeMax) : this.centsToDecimal(this.maxValue);
    }

    if (this.inputMin) {
      if (isDefault) {
        this.inputMin.removeAttribute('name');
        this.inputMin.value = '';
      } else {
        this.inputMin.setAttribute('name', this.minParamName);
        this.inputMin.value = this.centsToDecimal(this.minValue);
      }
    }

    if (this.inputMax) {
      if (isDefault) {
        this.inputMax.removeAttribute('name');
        this.inputMax.value = '';
      } else {
        this.inputMax.setAttribute('name', this.maxParamName);
        this.inputMax.value = this.centsToDecimal(this.maxValue);
      }
    }
  }

  private updateDragBounds(): void {
    const trackWidth = this.getTrackWidth();
    const minThumbMaxX = this.valueToX(this.maxValue);
    const maxThumbMinX = this.valueToX(this.minValue);

    this.minDraggable?.applyBounds({ minX: 0, maxX: minThumbMaxX });
    this.maxDraggable?.applyBounds({ minX: maxThumbMinX, maxX: trackWidth });
  }

  private initDraggables(): void {
    if (!this.slider || !this.thumbMin || !this.thumbMax) {
      return;
    }

    this.killDraggables();

    gsap.set([this.thumbMin, this.thumbMax], { x: 0, left: 0 });

    this.minDraggable = Draggable.create(this.thumbMin, {
      type: 'x',
      bounds: this.slider,
      onDrag: () => {
        const nextValue = this.clampCents(this.xToValue(this.minDraggable?.x ?? 0));
        this.minValue = Math.min(nextValue, this.maxValue);
        this.updateThumbPositions();
        this.updateAriaValues();
        this.syncFormInputs();
        this.updateDragBounds();
      },
      onDragEnd: () => {
        this.commitChangeDebounced();
      },
    })[0];

    this.maxDraggable = Draggable.create(this.thumbMax, {
      type: 'x',
      bounds: this.slider,
      onDrag: () => {
        const nextValue = this.clampCents(this.xToValue(this.maxDraggable?.x ?? 0));
        this.maxValue = Math.max(nextValue, this.minValue);
        this.updateThumbPositions();
        this.updateAriaValues();
        this.syncFormInputs();
        this.updateDragBounds();
      },
      onDragEnd: () => {
        this.commitChangeDebounced();
      },
    })[0];

    this.updateThumbPositions();
    this.updateDragBounds();
  }

  private killDraggables(): void {
    this.minDraggable?.kill();
    this.maxDraggable?.kill();
    this.minDraggable = null;
    this.maxDraggable = null;
  }

  private addEventListeners(): void {
    window.addEventListener('resize', this.onResizeBound);

    this.thumbMin?.addEventListener('keydown', this.onThumbMinKeyDownBound);
    this.thumbMax?.addEventListener('keydown', this.onThumbMaxKeyDownBound);
  }

  private removeEventListeners(): void {
    window.removeEventListener('resize', this.onResizeBound);

    this.thumbMin?.removeEventListener('keydown', this.onThumbMinKeyDownBound);
    this.thumbMax?.removeEventListener('keydown', this.onThumbMaxKeyDownBound);
  }

  private onResize(): void {
    this.initDraggables();
  }

  private onThumbKeyDown(event: KeyboardEvent, which: 'min' | 'max'): void {
    const step = event.shiftKey ? STEP_CENTS * 10 : STEP_CENTS;
    let nextValue = which === 'min' ? this.minValue : this.maxValue;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        nextValue -= step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        nextValue += step;
        break;
      case 'Home':
        nextValue = 0;
        break;
      case 'End':
        nextValue = this.rangeMax;
        break;
      default:
        return;
    }

    event.preventDefault();

    if (which === 'min') {
      this.minValue = Math.min(this.clampCents(nextValue), this.maxValue);
    } else {
      this.maxValue = Math.max(this.clampCents(nextValue), this.minValue);
    }

    this.syncUi(true);
  }

  private commitChangeDebounced(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.form?.dispatchEvent(new Event('change', { bubbles: true }));
    }, DEBOUNCE_MS);
  }

  public reset(): void {
    this.minValue = 0;
    this.maxValue = this.rangeMax;
    this.syncUi(false);
    this.initDraggables();
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.killDraggables();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
}

customElements.define('filter-range', FilterRange);
